import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
  TablePagination,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tab,
  Tabs,
  Badge,
} from "@mui/material";
import {
  ElectricBolt as SolarIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  PriorityHigh as RejectIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  LocalShipping,
  RestartAlt as RestartIcon,
} from "@mui/icons-material";
import {
  loadingAlert,
  closeAlert,
  successAlert,
  errorAlert,
  confirmAlert,
} from "../../utils/swalConfig";
import api from "../../utils/api";

const AdminSolarDashboard = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [detailsTabValue, setDetailsTabValue] = useState(0);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [requiredReferrals, setRequiredReferrals] = useState(3);
  const [savingSettings, setSavingSettings] = useState(false);
  const [viewMode, setViewMode] = useState("transactions"); // "transactions", "qualified", "shipped", "delivered"
  const [shippingStatusDialog, setShippingStatusDialog] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [shippingNotes, setShippingNotes] = useState("");
  const [shippingDate, setShippingDate] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    qualified: 0,
    addressSubmitted: 0,
    shipped: 0,
    delivered: 0,
  });

  useEffect(() => {
    fetchTransactions();
    fetchSettings();
  }, []);

  const calculateStats = (transactions) => {
    const stats = {
      pending: 0,
      approved: 0,
      rejected: 0,
      qualified: 0,
      addressSubmitted: 0,
      shipped: 0,
      delivered: 0,
    };

    transactions.forEach((transaction) => {
      // Count by transaction status
      if (transaction.status === "pending") stats.pending++;
      if (transaction.status === "approved") stats.approved++;
      if (transaction.status === "rejected") stats.rejected++;

      // Count qualified users (who have met referral requirements)
      if (transaction.program?.isEligible) stats.qualified++;

      // Count users who submitted shipping address
      if (transaction.program?.shippingAddress?.street)
        stats.addressSubmitted++;

      // Count by shipping status
      if (transaction.program?.status === "shipped") stats.shipped++;
      if (transaction.program?.status === "delivered") stats.delivered++;
    });

    setStats(stats);
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await api.get("/solar-program/transactions");
      const transactions = response.data.data.transactions || [];
      setTransactions(transactions);

      // Calculate stats from transactions
      calculateStats(transactions);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch transactions");
      console.error("Error fetching transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get("/solar-program/settings");
      if (response.data.status === "success") {
        setRequiredReferrals(response.data.data.requiredReferrals);
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  };

  const saveSettings = async () => {
    try {
      setSavingSettings(true);
      await api.patch("/solar-program/settings", {
        requiredReferrals: parseInt(requiredReferrals, 10),
      });
      successAlert("Success", "Solar program settings updated successfully");
      setSettingsDialog(false);
      fetchTransactions(); // Refresh data
    } catch (err) {
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to update settings"
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const handleApprove = async (transactionId) => {
    try {
      const result = await confirmAlert({
        title: "Confirm Approval",
        text: "Are you sure you want to approve this deposit?",
        icon: "warning",
        confirmButtonText: "Yes, Approve",
      });

      if (result.isConfirmed) {
        loadingAlert("Approving deposit...");
        await api.patch(`/solar-program/transactions/${transactionId}/approve`);
        successAlert("Success", "Deposit approved successfully");
        fetchTransactions();
      }
    } catch (err) {
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to approve deposit"
      );
    }
  };

  const handleReject = async (transaction) => {
    setSelectedTransaction(transaction);
    setRejectDialog(true);
  };

  const submitRejection = async () => {
    try {
      if (!rejectionReason.trim()) {
        return errorAlert("Error", "Please provide a reason for rejection");
      }

      loadingAlert("Rejecting deposit...");
      await api.patch(
        `/solar-program/transactions/${selectedTransaction._id}/reject`,
        {
          reason: rejectionReason,
        }
      );
      successAlert("Success", "Deposit rejected successfully");
      setRejectDialog(false);
      setRejectionReason("");
      fetchTransactions();
    } catch (err) {
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to reject deposit"
      );
    }
  };

  const handleDelete = async (transactionId) => {
    try {
      const result = await confirmAlert({
        title: "Delete Transaction",
        text: "Are you sure you want to delete this transaction? This action cannot be undone.",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it",
        cancelButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#d33",
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        loadingAlert("Deleting transaction...");
        await api.delete(`/solar-program/transactions/${transactionId}`);
        closeAlert();
        successAlert("Deleted!", "Transaction has been deleted successfully");
        fetchTransactions();
      }
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to delete transaction"
      );
    }
  };

  const handleUserDetails = async (userId, transaction) => {
    try {
      setDetailsDialog(true);
      setUserLoading(true);

      // Get user details including referrals
      const response = await api.get(
        `/admin/users/${userId}?includeReferrals=true`
      );
      const userData = response.data.data.user;

      // Add transaction data to the user details
      setUserDetails({
        ...userData,
        currentTransaction: transaction,
      });
    } catch (err) {
      errorAlert("Error", "Failed to fetch user details");
    } finally {
      setUserLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setDetailsTabValue(newValue);
  };

  const handleShippingStatusUpdate = async () => {
    try {
      if (!selectedTransaction) return;

      const payload = {
        status: viewMode === "qualified" ? "shipped" : "delivered",
        trackingNumber: trackingNumber || undefined,
        notes: shippingNotes || undefined,
        shippingDate: shippingDate || undefined,
        deliveryDate:
          viewMode === "shipped" ? deliveryDate || undefined : undefined,
      };

      await api.patch(
        `/solar-program/update-shipping/${selectedTransaction.program._id}`,
        payload
      );

      successAlert(
        "Success",
        viewMode === "qualified"
          ? "Package has been marked as shipped"
          : "Package has been marked as delivered"
      );

      // Close dialog and refresh data
      setShippingStatusDialog(false);
      fetchTransactions();
    } catch (err) {
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to update shipping status"
      );
    }
  };

  const handleShowShippingDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setTrackingNumber(transaction.program?.trackingNumber || "");
    setShippingNotes(transaction.program?.shippingNotes || "");
    setShippingDate(transaction.program?.shippingDate?.split("T")[0] || "");
    setDeliveryDate(transaction.program?.deliveryDate?.split("T")[0] || "");
    setShippingStatusDialog(true);
  };

  // Modified handleResetSolarProgram function with better UX
  const handleResetSolarProgram = async (userId) => {
    try {
      // First show the confirmation dialog
      const result = await confirmAlert({
        title: "Reset Solar Program",
        text: "This will reset the user's solar program status. All shipping, delivery, and eligibility data will be lost. Continue?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, reset it",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#d33",
        reverseButtons: true,
      });

      // Only proceed if the user confirmed
      if (result.isConfirmed) {
        // Show loading indicator but DO NOT close the details dialog yet
        loadingAlert("Resetting solar program...");

        // Make the API request
        await api.patch(`/solar-program/reset-user/${userId}`);

        // First close the loading alert
        closeAlert();

        // Then show success message
        await successAlert(
          "Success",
          "Solar program has been reset successfully"
        );

        // Now fetch the updated data
        await fetchTransactions();

        // Only close the details dialog after everything is complete
        setDetailsDialog(false);
      }
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to reset solar program"
      );
      // Keep the details dialog open if there's an error
    }
  };

  // Update the getFilteredList function with a more accurate qualified users filter
  const getFilteredList = () => {
    let filteredList = [];

    if (viewMode === "transactions") {
      filteredList = transactions;
    } else if (viewMode === "qualified") {
      filteredList = transactions.filter((t) => {
        // A user is qualified if they:
        // 1. Have made a deposit that's been approved
        // 2. Have met the referral requirements (isEligible is true)
        // 3. Haven't been shipped yet (status isn't "shipped" or "delivered")
        return (
          t.program &&
          t.program.isEligible === true &&
          t.status === "approved" &&
          (!t.program.status || t.program.status === "pending")
        );
      });
    } else if (viewMode === "shipped") {
      filteredList = transactions.filter(
        (t) => t.program?.status === "shipped"
      );
    } else if (viewMode === "delivered") {
      filteredList = transactions.filter(
        (t) => t.program?.status === "delivered"
      );
    }

    // Apply search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredList = filteredList.filter(
        (item) =>
          item?.user?.name?.toLowerCase().includes(searchLower) ||
          item?.user?.email?.toLowerCase().includes(searchLower) ||
          item?.transactionReference?.toLowerCase().includes(searchLower) ||
          item?.program?.trackingNumber?.toLowerCase()?.includes(searchLower)
      );
    }

    return filteredList;
  };

  // Update paginatedTransactions to use this method
  const paginatedTransactions = getFilteredList().slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Update the renderTabs function to use Badge component for better visibility
  const renderTabs = () => (
    <Paper sx={{ mb: 2 }}>
      <Tabs
        value={viewMode}
        onChange={(e, newValue) => {
          setViewMode(newValue);
          setPage(0); // Reset to first page when changing tabs
        }}
        variant="fullWidth"
      >
        <Tab
          label={
            <Badge badgeContent={stats.pending} color="warning" max={99}>
              <Box sx={{ pr: 1 }}>Transactions</Box>
            </Badge>
          }
          value="transactions"
        />
        <Tab
          label={
            <Badge badgeContent={stats.qualified} color="success" max={99}>
              <Box sx={{ pr: 1 }}>Qualified</Box>
            </Badge>
          }
          value="qualified"
        />
        <Tab
          label={
            <Badge badgeContent={stats.shipped} color="primary" max={99}>
              <Box sx={{ pr: 1 }}>Shipped</Box>
            </Badge>
          }
          value="shipped"
        />
        <Tab
          label={
            <Badge badgeContent={stats.delivered} color="info" max={99}>
              <Box sx={{ pr: 1 }}>Delivered</Box>
            </Badge>
          }
          value="delivered"
        />
      </Tabs>
    </Paper>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Solar Program Management</Typography>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setSettingsDialog(true)}
        >
          Program Settings
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h4" color="warning.main">
                      {stats.pending}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending Deposits
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h4" color="success.main">
                      {stats.qualified}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Qualified Users
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h4" color="primary.main">
                      {stats.shipped}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Shipped Systems
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h4" color="info.main">
                      {stats.delivered}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Delivered Systems
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Search Box */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, email, or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchTerm("")} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        </Grid>

        {/* Add the tabs component */}
        <Grid item xs={12}>
          {renderTabs()}
        </Grid>

        {/* Replace the existing table with conditional rendering based on viewMode */}
        <Grid item xs={12}>
          {viewMode === "transactions" && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Valid Referrals</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map(
                    (transaction) =>
                      transaction && (
                        <TableRow key={transaction._id}>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Box>
                                <Typography variant="body2">
                                  {transaction.user?.name || "N/A"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {transaction.user?.email || "N/A"}
                                </Typography>
                              </Box>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  sx={{ ml: 1 }}
                                  onClick={() =>
                                    handleUserDetails(
                                      transaction.user?._id,
                                      transaction
                                    )
                                  }
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {transaction.transactionReference}
                          </TableCell>
                          <TableCell>
                            ₦{transaction.amount?.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {new Date(
                              transaction.createdAt
                            ).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.status}
                              color={getStatusColor(transaction.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {transaction.program?.referralCount || 0}/
                            {requiredReferrals}
                          </TableCell>
                          <TableCell align="right">
                            {transaction.status === "pending" && (
                              <Box>
                                <Tooltip title="Approve">
                                  <IconButton
                                    color="success"
                                    onClick={() =>
                                      handleApprove(transaction._id)
                                    }
                                  >
                                    <CheckIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <IconButton
                                    color="error"
                                    onClick={() => handleReject(transaction)}
                                  >
                                    <RejectIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                            {transaction.status !== "pending" && (
                              <Tooltip title="Delete">
                                <IconButton
                                  color="error"
                                  onClick={() => handleDelete(transaction._id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                  )}
                  {paginatedTransactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {getFilteredList().length === 0 && searchTerm
                            ? "No matching transactions found"
                            : "No transactions found"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={getFilteredList().length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}

          {viewMode === "qualified" && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Valid Referrals</TableCell>
                    <TableCell>Shipping Address</TableCell>
                    <TableCell>Contact Phone</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map((transaction) => (
                    <TableRow key={transaction._id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box>
                            <Typography variant="body2">
                              {transaction.user?.name || "N/A"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {transaction.user?.email || "N/A"}
                            </Typography>
                          </Box>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              sx={{ ml: 1 }}
                              onClick={() =>
                                handleUserDetails(
                                  transaction.user?._id,
                                  transaction
                                )
                              }
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {transaction.program?.referralCount || 0}/
                        {requiredReferrals}
                      </TableCell>
                      <TableCell>
                        {transaction.program?.shippingAddress?.street ? (
                          <Chip
                            label="Address Provided"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="No Address"
                            color="warning"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {transaction.program?.shippingAddress?.phoneNumber ||
                          "N/A"}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={
                            !transaction.program?.shippingAddress?.street
                          }
                          onClick={() => handleShowShippingDialog(transaction)}
                          startIcon={<LocalShipping />}
                          size="small"
                        >
                          Mark as Shipped
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedTransactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No qualified users found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={getFilteredList().length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}

          {viewMode === "shipped" && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Shipping Address</TableCell>
                    <TableCell>Tracking Number</TableCell>
                    <TableCell>Shipped Date</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map((transaction) => (
                    <TableRow key={transaction._id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box>
                            <Typography variant="body2">
                              {transaction.user?.name || "N/A"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {transaction.user?.email || "N/A"}
                            </Typography>
                          </Box>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              sx={{ ml: 1 }}
                              onClick={() =>
                                handleUserDetails(
                                  transaction.user?._id,
                                  transaction
                                )
                              }
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {transaction.program?.shippingAddress ? (
                          <Tooltip
                            title={`${
                              transaction.program?.shippingAddress?.street
                            }, ${
                              transaction.program?.shippingAddress?.lga ||
                              transaction.program?.shippingAddress?.city
                            }, ${transaction.program?.shippingAddress?.state}`}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 200 }}
                            >
                              {transaction.program?.shippingAddress?.street},{" "}
                              {transaction.program?.shippingAddress?.lga ||
                                transaction.program?.shippingAddress?.city}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography>N/A</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {transaction.program?.trackingNumber || "N/A"}
                      </TableCell>
                      <TableCell>
                        {transaction.program?.shippingDate
                          ? new Date(
                              transaction.program.shippingDate
                            ).toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            transaction.program?.shippingNotes || "No notes"
                          }
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 150 }}
                          >
                            {transaction.program?.shippingNotes || "No notes"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<CheckIcon />}
                          onClick={() => handleShowShippingDialog(transaction)}
                        >
                          Mark as Delivered
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedTransactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {getFilteredList().length === 0 && searchTerm
                            ? "No matching shipped packages found"
                            : "No shipped packages found"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={getFilteredList().length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}

          {viewMode === "delivered" && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Shipping Address</TableCell>
                    <TableCell>Tracking Number</TableCell>
                    <TableCell>Shipped Date</TableCell>
                    <TableCell>Delivered Date</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map((transaction) => (
                    <TableRow key={transaction._id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box>
                            <Typography variant="body2">
                              {transaction.user?.name || "N/A"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {transaction.user?.email || "N/A"}
                            </Typography>
                          </Box>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              sx={{ ml: 1 }}
                              onClick={() =>
                                handleUserDetails(
                                  transaction.user?._id,
                                  transaction
                                )
                              }
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {transaction.program?.shippingAddress ? (
                          <Tooltip
                            title={`${
                              transaction.program?.shippingAddress?.street
                            }, ${
                              transaction.program?.shippingAddress?.lga ||
                              transaction.program?.shippingAddress?.city
                            }, ${transaction.program?.shippingAddress?.state}`}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 200 }}
                            >
                              {transaction.program?.shippingAddress?.street},{" "}
                              {transaction.program?.shippingAddress?.lga ||
                                transaction.program?.shippingAddress?.city}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography>N/A</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {transaction.program?.trackingNumber || "N/A"}
                      </TableCell>
                      <TableCell>
                        {transaction.program?.shippingDate
                          ? new Date(
                              transaction.program.shippingDate
                            ).toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        {transaction.program?.deliveryDate
                          ? new Date(
                              transaction.program.deliveryDate
                            ).toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            transaction.program?.shippingNotes || "No notes"
                          }
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 150 }}
                          >
                            {transaction.program?.shippingNotes || "No notes"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedTransactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {getFilteredList().length === 0 && searchTerm
                            ? "No matching delivered packages found"
                            : "No delivered packages found"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={getFilteredList().length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}

          {/* Similar tables for shipped and delivered */}
        </Grid>
      </Grid>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
        <DialogTitle>Reject Deposit</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
          <Button onClick={submitRejection} color="error" variant="contained">
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          User Details
          {userLoading && (
            <CircularProgress
              size={24}
              sx={{ ml: 2, verticalAlign: "middle" }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {userDetails && !userLoading ? (
            <>
              <Tabs
                value={detailsTabValue}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
              >
                <Tab
                  label="Basic Info"
                  icon={<InfoIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Shipping Address"
                  icon={<LocationIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Referrals"
                  icon={<GroupIcon />}
                  iconPosition="start"
                />
              </Tabs>

              {detailsTabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        User Information
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Name"
                            secondary={userDetails.name}
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText
                            primary="Email"
                            secondary={userDetails.email}
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText
                            primary="Phone"
                            secondary={
                              userDetails.phoneNumber || "Not provided"
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText
                            primary="Referral Code"
                            secondary={
                              userDetails.referralCode || "Not available"
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText
                            primary="Referred By"
                            secondary={userDetails.referrer || "Not referred"}
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText
                            primary="Account Created"
                            secondary={new Date(
                              userDetails.createdAt
                            ).toLocaleString()}
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Program Status
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Valid Referrals"
                            secondary={`${
                              userDetails.currentTransaction?.program
                                ?.referralCount || 0
                            }/${requiredReferrals}`}
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText
                            primary="Eligibility Status"
                            secondary={
                              <Chip
                                label={
                                  (userDetails.currentTransaction?.program
                                    ?.referralCount || 0) >= requiredReferrals
                                    ? "Eligible"
                                    : "Not Eligible"
                                }
                                color={
                                  (userDetails.currentTransaction?.program
                                    ?.referralCount || 0) >= requiredReferrals
                                    ? "success"
                                    : "warning"
                                }
                                size="small"
                              />
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText
                            primary="Transaction Reference"
                            secondary={
                              userDetails.currentTransaction
                                ?.transactionReference || "N/A"
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText
                            primary="Transaction Amount"
                            secondary={`₦${
                              userDetails.currentTransaction?.amount?.toLocaleString() ||
                              "0"
                            }`}
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText
                            primary="Transaction Status"
                            secondary={
                              <Chip
                                label={
                                  userDetails.currentTransaction?.status ||
                                  "N/A"
                                }
                                color={getStatusColor(
                                  userDetails.currentTransaction?.status
                                )}
                                size="small"
                              />
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText
                            primary="Deposit Date"
                            secondary={
                              userDetails.currentTransaction?.createdAt
                                ? new Date(
                                    userDetails.currentTransaction.createdAt
                                  ).toLocaleString()
                                : "N/A"
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText
                            primary="Program Actions"
                            secondary={
                              <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                startIcon={<RestartIcon />}
                                onClick={() =>
                                  handleResetSolarProgram(
                                    userDetails.user?._id || userDetails._id
                                  )
                                }
                                sx={{ mt: 1 }}
                              >
                                Reset Solar Program
                              </Button>
                            }
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {detailsTabValue === 1 && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Shipping Address
                  </Typography>
                  {userDetails.currentTransaction?.program?.shippingAddress ? (
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Street"
                          secondary={
                            userDetails.currentTransaction.program
                              .shippingAddress.street || "Not provided"
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText
                          primary="Local Government Area (LGA)"
                          secondary={
                            userDetails.currentTransaction.program
                              .shippingAddress.lga ||
                            userDetails.currentTransaction.program
                              .shippingAddress.city ||
                            "Not provided"
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText
                          primary="State"
                          secondary={
                            userDetails.currentTransaction.program
                              .shippingAddress.state || "Not provided"
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText
                          primary="Contact Phone"
                          secondary={
                            userDetails.currentTransaction.program
                              .shippingAddress.phoneNumber || "Not provided"
                          }
                        />
                      </ListItem>
                    </List>
                  ) : (
                    <Alert severity="info">
                      This user has not provided shipping address information
                      yet.
                    </Alert>
                  )}
                </Paper>
              )}

              {detailsTabValue === 2 && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Referrals
                  </Typography>
                  {userDetails.referrals && userDetails.referrals.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Join Date</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userDetails.referrals.map((referral) => (
                            <TableRow key={referral._id}>
                              <TableCell>{referral.name}</TableCell>
                              <TableCell>{referral.email}</TableCell>
                              <TableCell>
                                {new Date(
                                  referral.createdAt
                                ).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    referral.hasDeposited ? "Active" : "Pending"
                                  }
                                  color={
                                    referral.hasDeposited
                                      ? "success"
                                      : "warning"
                                  }
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">
                      This user has not referred anyone yet.
                    </Alert>
                  )}
                </Paper>
              )}
            </>
          ) : !userLoading ? (
            <Alert severity="error">Failed to load user details</Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          {userDetails && userDetails.currentTransaction && (
            <Button
              onClick={() =>
                handleResetSolarProgram(
                  userDetails.user?._id || userDetails._id
                )
              }
              color="warning"
              variant="outlined"
              startIcon={<RestartIcon />}
              sx={{ mr: "auto" }}
            >
              Reset Solar Program
            </Button>
          )}
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onClose={() => setSettingsDialog(false)}>
        <DialogTitle>Solar Program Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              type="number"
              label="Required Referrals"
              value={requiredReferrals}
              onChange={(e) => setRequiredReferrals(e.target.value)}
              InputProps={{
                inputProps: { min: 1 },
              }}
              helperText="Number of valid referrals required to qualify for the solar system"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog(false)}>Cancel</Button>
          <Button
            onClick={saveSettings}
            color="primary"
            variant="contained"
            disabled={savingSettings}
          >
            {savingSettings ? "Saving..." : "Save Settings"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add this dialog for shipping status updates */}
      <Dialog
        open={shippingStatusDialog}
        onClose={() => setShippingStatusDialog(false)}
      >
        <DialogTitle>
          {viewMode === "qualified" ? "Mark as Shipped" : "Mark as Delivered"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {viewMode === "qualified" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    label="Tracking Number"
                    fullWidth
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    helperText="Enter the shipping tracking number (optional)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Shipping Date"
                    type="date"
                    fullWidth
                    value={shippingDate}
                    onChange={(e) => setShippingDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave blank to use today's date"
                  />
                </Grid>
              </>
            )}

            {viewMode === "shipped" && (
              <Grid item xs={12}>
                <TextField
                  label="Delivery Date"
                  type="date"
                  fullWidth
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave blank to use today's date"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={shippingNotes}
                onChange={(e) => setShippingNotes(e.target.value)}
                helperText="Any additional information (optional)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShippingStatusDialog(false)}>Cancel</Button>
          <Button
            onClick={handleShippingStatusUpdate}
            variant="contained"
            color={viewMode === "qualified" ? "primary" : "success"}
          >
            {viewMode === "qualified" ? "Mark as Shipped" : "Mark as Delivered"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSolarDashboard;
